
/**
 * @fileoverview Hook UI Layer para scroll paginado infinito optimizado.
 * Encapsula la lógica visual de scroll para paginación automática en listas grandes.
 * Cumple arquitectura Mercately: separación de capas, memoización y performance.
 *
 * @module usePaginatedScrollUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 2.0.0 - JSDoc completo, typedefs, ejemplos y optimización avanzada
 *
 * @typedef {Object} PaginatedScrollUIParams
 * @property {boolean} loading - Indica si se está cargando la siguiente página
 * @property {Object} pagination - Objeto de paginación con current_page y has_next_page
 * @property {function} onNextPage - Callback para cargar la siguiente página (recibe el número de página)
 * @property {number} [threshold=50] - Distancia en px al borde para disparar la paginación
 * @property {'down'|'up'} [direction='down'] - Dirección de scroll para paginación ('down' o 'up')
 *
 * @typedef {Object} PaginatedScrollUIResult
 * @property {function} containerRef - Ref callback para el contenedor scrollable
 * @property {function} handleScroll - Handler para el evento onScroll
 */

import { useCallback, useEffect, useRef } from 'react';


/**
 * Hook UI Layer para scroll paginado infinito en listas.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades:
 * - Solo maneja estado visual y eventos de scroll para paginación.
 * - No realiza llamadas a API ni lógica de negocio.
 * - Optimización avanzada: refs, memoización y efectos controlados.
 *
 * @function usePaginatedScrollUI
 * @category Hook - UI Layer
 *
 * @description
 * **Arquitectura Optimizada (v2.0.0):**
 * - ✅ Solo UI Layer: scroll, refs y bloqueo visual
 * - ✅ Memoización con useCallback para performance
 * - ✅ Efectos controlados y limpieza de refs
 * - ✅ Sin lógica de negocio ni API
 *
 * **Responsabilidades:**
 * 1. Detectar proximidad al borde (top/bottom) para disparar paginación
 * 2. Bloquear múltiples disparos mientras loading
 * 3. Ajustar scroll al cargar más elementos (especialmente en scroll hacia arriba)
 *
 * **NO Maneja:**
 * - ❌ Lógica de negocio (carga de datos, mutaciones)
 * - ❌ Llamadas a API
 *
 * **Flujo de Operación:**
 * 1. containerRef se asigna al contenedor scrollable
 * 2. handleScroll detecta si se debe cargar la siguiente página
 * 3. Efectos desbloquean y ajustan scroll tras carga
 *
 * @param {PaginatedScrollUIParams} params - Parámetros de entrada del hook
 * @param {boolean} params.loading - Estado de carga de la siguiente página
 * @param {Object} params.pagination - Objeto de paginación
 * @param {function} params.onNextPage - Callback para cargar la siguiente página
 * @param {number} [params.threshold=50] - Distancia al borde para disparar
 * @param {'down'|'up'} [params.direction='down'] - Dirección de scroll
 *
 * @returns {PaginatedScrollUIResult} Objeto con ref y handler de scroll
 *
 * @see {@link ../../../../modules/inbox/AreaChatWindowMC.js} Ejemplo de uso en chat
 *
 * @since 1.0.0
 * @version 2.0.0 - JSDoc completo, typedefs, ejemplos y optimización avanzada
 *
 * @example
 * // Uso básico en un componente de lista
 * const { containerRef, handleScroll } = usePaginatedScrollUI({
 *   loading,
 *   pagination,
 *   onNextPage,
 *   threshold: 100,
 *   direction: 'down'
 * });
 *
 * <div ref={containerRef} onScroll={handleScroll}>
 *   {items.map(...)}
 * </div>
 */
export default function usePaginatedScrollUI({
  loading,
  pagination,
  onNextPage,
  threshold = 50,
  direction = 'down'
}) {
  /**
   * Ref de bloqueo para evitar múltiples disparos de paginación.
   * @type {React.MutableRefObject<boolean>}
   * @private
   * @memberof usePaginatedScrollUI
   */
  const lock = useRef(false);

  /**
   * Ref al contenedor scrollable.
   * @type {React.MutableRefObject<HTMLElement|null>}
   * @private
   * @memberof usePaginatedScrollUI
   */
  const scrollContainer = useRef(null);

  /**
   * Ref para guardar el scrollHeight previo (solo para scroll hacia arriba).
   * @type {React.MutableRefObject<number|null>}
   * @private
   * @memberof usePaginatedScrollUI
   */
  const prevScrollHeight = useRef(null);

  /**
   * Callback ref para asignar el contenedor scrollable.
   *
   * @function containerRef
   * @memberof usePaginatedScrollUI
   * @param {HTMLElement|null} node - Nodo DOM del contenedor
   * @returns {void}
   *
   * @example
   * <div ref={containerRef} ... />
   */
  const containerRef = useCallback(node => {
    if (node) scrollContainer.current = node;
  }, []);

  /**
   * Handler para el evento onScroll del contenedor.
   *
   * @function handleScroll
   * @memberof usePaginatedScrollUI
   * @param {React.UIEvent} e - Evento de scroll
   * @returns {void}
   *
   * @description
   * - Detecta si el usuario está cerca del borde (top/bottom) según direction y threshold.
   * - Si corresponde, dispara onNextPage y bloquea nuevos disparos hasta que loading sea false.
   *
   * @example
   * <div onScroll={handleScroll} ... />
   */
  const handleScroll = useCallback((e) => {
    if (lock.current || loading) return;

    const el = e.target;
    const { scrollTop, scrollHeight, clientHeight } = el;

    if (direction === 'down') {
      const nearBottom = scrollHeight - scrollTop - clientHeight < threshold;

      if (nearBottom && pagination?.has_next_page) {
        lock.current = true;
        onNextPage?.(pagination.current_page + 1);
      }
    }

    if (direction === 'up') {
      const nearTop = scrollTop < threshold;

      if (nearTop && pagination?.has_next_page) {
        prevScrollHeight.current = scrollHeight;
        lock.current = true;
        onNextPage?.(pagination.current_page + 1);
      }
    }
  }, [loading, pagination, onNextPage, threshold, direction]);

  /**
   * Efecto para desbloquear el lock tras finalizar loading.
   * @private
   * @memberof usePaginatedScrollUI
   */
  useEffect(() => {
    if (!loading) {
      lock.current = false;
    }
  }, [loading]);

  /**
   * Efecto para ajustar el scroll tras cargar más elementos (solo en scroll hacia arriba).
   * @private
   * @memberof usePaginatedScrollUI
   */
  useEffect(() => {
    if (
      direction === 'up' &&
      prevScrollHeight.current &&
      scrollContainer.current
    ) {
      const el = scrollContainer.current;

      const newHeight = el.scrollHeight;
      const diff = newHeight - prevScrollHeight.current;

      el.scrollTop = diff;

      prevScrollHeight.current = null;
    }
  }, [direction, pagination?.current_page]);

  /**
   * Devuelve el ref y handler para scroll paginado.
   *
   * @returns {PaginatedScrollUIResult} Objeto con ref y handler
   *
   * @example
   * const { containerRef, handleScroll } = usePaginatedScrollUI(...);
   */
  return {
    containerRef,
    handleScroll
  };
}
